import { resetProbeSegmentUrl } from './shakaLoader';

export const VIDEO_CACHE_NAME = 'bifrost-video-segments';
export const VIDEO_CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
const VIDEO_CACHE_MAX_ENTRIES = 150;

// A session that opens over the cap drains a few entries per write instead of
// hundreds at once: the first write lands while the player is still fetching
// the opening segments, and a burst of deletes competes with them.
const EVICTIONS_PER_WRITE = 8;

// _cb=1 is appended by getProxiedVideoUrl for CORS preflight busting but is
// not part of the canonical resource identity. Stripping it ensures that
// prefetch (which uses _cb) and Shaka playback (which does not) share the
// same cache entry.
function normalizeCacheUrl(url: string): string {
	try {
		const parsed = new URL(url);
		parsed.searchParams.delete('_cb');
		return parsed.toString();
	} catch {
		return url;
	}
}

// CacheStorage survives a reload but this Set does not. Seeding it from the
// store is what makes VIDEO_CACHE_MAX_ENTRIES mean anything: without it every
// page load restarts the counter at zero, the cap is never reached and the
// cache grows without bound on the user's device (measured: 224 entries against
// a cap of 150).
//
// Nothing ever waits for this: the warm chain writes off the player's path, and
// a wait here would sit in front of the warm-up of every session.
const cachedKeys = new Set<string>();
let hydration: Promise<void> | null = null;
// Bumped by clearVideoCache so a hydration still in flight cannot repopulate
// the set with keys of a store that no longer exists.
let cacheGeneration = 0;

function hydrateCachedKeys(cache: Cache): void {
	if (hydration) return;
	const generation = cacheGeneration;
	hydration = cache
		.keys()
		.then(requests => {
			if (generation !== cacheGeneration) return;
			// Order matters: eviction walks the set from the front, and whatever
			// came from the store is older than anything written in this session.
			// Appending would evict the segments the user just paid for and keep
			// the stale ones.
			const written = [...cachedKeys];
			cachedKeys.clear();
			for (const request of requests) cachedKeys.add(request.url);
			for (const key of written) cachedKeys.add(key);
		})
		.catch(() => {
			// Let a later write try again. A memoised failure would leave the set
			// empty and silently disable the cap for the rest of the session.
			hydration = null;
		});
}

// Maximum time to wait for a CacheStorage read. On mobile under disk pressure,
// cache.match() can hang for tens of seconds — fall through to network fetch
// rather than blocking the segment download.
const CACHE_READ_TIMEOUT_MS = 800;

const TIMED_OUT = Symbol('cache read timed out');

export async function getVideoCache(): Promise<Cache | null> {
	if (!('caches' in globalThis)) return null;
	return Promise.race([
		caches.open(VIDEO_CACHE_NAME),
		new Promise<null>(resolve =>
			setTimeout(() => resolve(null), CACHE_READ_TIMEOUT_MS)
		)
	]);
}

/**
 * Whether this session wrote the URL (or found it when hydrating). Synchronous,
 * so the player skips CacheStorage on a miss, which is most segments, instead
 * of paying an open and a match that can each take 800 ms on a phone under
 * disk pressure. Entries of an earlier session are left to the HTTP cache.
 */
export function isVideoCached(url: string): boolean {
	return cachedKeys.has(normalizeCacheUrl(url));
}

export async function getCachedResponse(url: string): Promise<Response | null> {
	const cache = await getVideoCache();
	if (!cache) return null;

	const key = normalizeCacheUrl(url);

	const cached = await Promise.race([
		cache.match(key),
		new Promise<typeof TIMED_OUT>(resolve =>
			setTimeout(() => resolve(TIMED_OUT), CACHE_READ_TIMEOUT_MS)
		)
	]);
	// A timeout says nothing about whether the entry exists, so the key stays.
	if (cached === TIMED_OUT) return null;

	if (!cached) {
		// The store answered and does not have it, which happens when the browser
		// drops entries under storage pressure. Keeping the key counted would make
		// this URL uncacheable for the rest of the session.
		cachedKeys.delete(key);
		return null;
	}

	const cachedAt = cached.headers.get('x-cached-at');
	if (cachedAt && Date.now() - Number(cachedAt) > VIDEO_CACHE_MAX_AGE_MS) {
		cache.delete(key).catch(() => {});
		cachedKeys.delete(key);
		return null;
	}

	return cached;
}

/** Consumes the response body: pass a clone if the caller still reads it. */
export async function cacheResponse(
	url: string,
	response: Response
): Promise<void> {
	const cache = await getVideoCache();
	if (!cache) return;

	hydrateCachedKeys(cache);

	const key = normalizeCacheUrl(url);
	if (cachedKeys.has(key)) return;

	const headers = new Headers(response.headers);
	headers.set('x-cached-at', String(Date.now()));

	// Streamed into the store: no extra copy of the segment in the JS heap.
	const cachedResponse = new Response(response.body, {
		status: response.status,
		statusText: response.statusText,
		headers
	});

	// Evict after the write, never before: a put that rejects on quota, on a 206
	// or on an opaque response would otherwise cost an entry with nothing taking
	// its place.
	await cache.put(key, cachedResponse);
	cachedKeys.add(key);

	let evicted = 0;
	for (const oldest of cachedKeys) {
		if (cachedKeys.size <= VIDEO_CACHE_MAX_ENTRIES) break;
		if (evicted++ >= EVICTIONS_PER_WRITE) break;
		cachedKeys.delete(oldest);
		// A delete that fails leaves the entry in the store uncounted. The next
		// session picks it up again through hydration.
		cache.delete(oldest).catch(() => {});
	}
}

export async function clearVideoCache(): Promise<void> {
	if (!('caches' in globalThis)) return;
	cacheGeneration++;
	cachedKeys.clear();
	hydration = null;
	// The probe reuses a cached segment URL — reset it so the next probe does
	// a real CDN fetch rather than hitting a URL no longer in cache.
	resetProbeSegmentUrl();
	await caches.delete(VIDEO_CACHE_NAME);
}
